import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useRepository } from '@/ui/state/repositoryContext'
import { useTelemetry } from '@/ui/state/telemetryContext'

// Heavy use cases lazy-load on the user's first click — see
// UploadButton for the same pattern.
const loadCreate = () =>
  import('@/application/library/createProjectFromUpload').then((m) => m.createProjectFromUpload)
const loadImport = () =>
  import('@/application/library/importNewVersion').then((m) => m.importNewVersion)

const SAMPLE_V1_PATH = '/sample/markfy-sample.md'
const SAMPLE_V2_PATH = '/sample/markfy-sample-v2.md'
const SAMPLE_NAME = 'markfy-sample.md'

type Mode = null | 'sample' | 'demo'

/**
 * Two side-by-side first-run CTAs (PRD #75, #76).
 *
 *   - "Try the sample"      — fetches v1 only, navigates into the
 *                             review surface so the user can see how
 *                             markfy renders + the gutter affordance.
 *   - "Try the diff demo"   — fetches v1, then immediately imports v2,
 *                             then navigates. The user lands with the
 *                             import banner already visible — the
 *                             headline diff/badge experience on day one.
 *
 * Both buttons hit only same-origin static assets (`/sample/`), so no
 * network surface is widened (Constraint #1 holds).
 */
export function SampleProjectButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const repo = useRepository()
  const telemetry = useTelemetry()
  const [busy, setBusy] = useState<Mode>(null)
  const [error, setError] = useState<string | null>(null)

  async function fetchSampleAsFile(path: string, name: string): Promise<File> {
    const response = await fetch(path)
    if (!response.ok) throw new Error(`Could not load ${name} (${response.status})`)
    const blob = await response.blob()
    return new File([blob], name, { type: 'text/markdown' })
  }

  async function loadSample() {
    setError(null)
    setBusy('sample')
    try {
      const createProjectFromUpload = await loadCreate()
      const v1 = await fetchSampleAsFile(SAMPLE_V1_PATH, SAMPLE_NAME)
      const { project } = await createProjectFromUpload(v1, repo)
      void navigate(`/p/${project.id}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(null)
    }
  }

  async function loadDemo() {
    setError(null)
    setBusy('demo')
    try {
      const [createProjectFromUpload, importNewVersion] = await Promise.all([
        loadCreate(),
        loadImport(),
      ])
      const v1 = await fetchSampleAsFile(SAMPLE_V1_PATH, SAMPLE_NAME)
      const { project } = await createProjectFromUpload(v1, repo)
      const v2 = await fetchSampleAsFile(SAMPLE_V2_PATH, SAMPLE_NAME)
      await importNewVersion(project.id, v2, repo, undefined, telemetry)
      void navigate(`/p/${project.id}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void loadSample()}
          disabled={busy != null}
          aria-busy={busy === 'sample'}
          className="rounded border px-4 py-2 text-sm"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-fg)',
            background: 'transparent',
          }}
        >
          {busy === 'sample' ? '…' : t('library.trySample')}
        </button>
        <button
          type="button"
          onClick={() => void loadDemo()}
          disabled={busy != null}
          aria-busy={busy === 'demo'}
          className="rounded px-4 py-2 text-sm"
          style={{
            background: 'var(--color-accent)',
            color: 'var(--color-accent-fg)',
          }}
        >
          {busy === 'demo' ? '…' : t('library.tryDiffDemo')}
        </button>
      </div>
      {error && (
        <div role="alert" className="text-xs" style={{ color: 'var(--color-down)' }}>
          {error}
        </div>
      )}
    </div>
  )
}
